import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Container, Card, Alert, Spinner, Button, Select, Badge, Icon } from '../../../../core/ui';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getStatement,
  getStatementTransactions,
  updateTransaction,
  listCategories,
  listCards,
  matchStatementReceipts,
} from '../../api/budget';
import { formatTry } from '../../format';
import { formatShortDate, formatLongMonth, monthKey, dateSortValue } from '../../../../core/format/date';
import SectionedTable from '../../../../core/common/SectionedTable';
import AmountCell from './AmountCell';
import ReceiptLinkModal from './ReceiptLinkModal';
import { useTranslation } from '../../../../core/context/TranslationContext';
import { useToast } from '../../../../core/context/ToastContext';
import styles from './StatementDetail.module.css';

const StatementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();

  const [statement, setStatement] = useState(null);
  const [card, setCard] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkModalTx, setLinkModalTx] = useState(null);
  const [matching, setMatching] = useState(false);
  const { toast } = useToast();

  const categoryLabel = (c) =>
    (language === 'tr' ? c.displayNameTr : c.displayNameEn) || c.key;

  const categoriesById = useMemo(() => {
    const m = {};
    for (const c of categories) m[c.id] = c;
    return m;
  }, [categories]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [s, tx, cats, cards] = await Promise.all([
        getStatement(id),
        getStatementTransactions(id),
        listCategories(),
        listCards(),
      ]);
      setStatement(s);
      setTransactions(tx);
      setCategories(cats);
      setCard(cards.find(c => c.id === s.cardId) || null);
    } catch (err) {
      setError(err?.message || t('error'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const onCategoryChange = async (txId, newCategoryId) => {
    try {
      const updated = await updateTransaction(txId, {
        categoryId: newCategoryId ? Number(newCategoryId) : null,
      });
      setTransactions(prev => prev.map(t => (t.id === txId ? updated : t)));
    } catch (err) {
      setError(err?.message || t('error'));
    }
  };

  const onTransactionUpdated = (updated) => {
    setTransactions(prev => prev.map(tx => (tx.id === updated.id ? updated : tx)));
  };

  const onLinkChanged = (txId, receiptLink) => {
    setTransactions(prev => prev.map(tx => (tx.id === txId ? { ...tx, receiptLink } : tx)));
  };

  const onMatchReceipts = async () => {
    try {
      setMatching(true);
      const { linked } = await matchStatementReceipts(id);
      setTransactions(await getStatementTransactions(id));
      toast(linked > 0 ? t('budget.matchReceiptsDone', { count: linked }) : t('budget.matchReceiptsNone'),
        { variant: linked > 0 ? 'success' : 'info' });
    } catch (err) {
      console.error('Receipt matching failed', err);
      toast(err?.message || t('error'), { variant: 'danger' });
    } finally {
      setMatching(false);
    }
  };

  const linkedCount = transactions.filter(tx => tx.receiptLink).length;
  const reviewCount = transactions.filter(tx => tx.amountNeedsReview).length;

  const txColumns = [
    {
      key: 'date',
      header: t('date'),
      accessor: (r) => dateSortValue(r.transactionDate),
      cell: (r) => formatShortDate(r.transactionDate, language),
    },
    {
      key: 'merchant',
      header: t('budget.merchant'),
      accessor: (r) => r.merchantNormalized || '',
      cell: (r) => (
        <div>
          <div>{r.merchantNormalized}</div>
          {r.merchantRaw && r.merchantRaw !== r.merchantNormalized && (
            <div className={styles.mutedSmall}>{r.merchantRaw}</div>
          )}
        </div>
      ),
    },
    {
      key: 'amount',
      header: t('budget.amount'),
      align: 'end',
      accessor: (r) => Number(r.amount || 0),
      cell: (r) => <AmountCell transaction={r} onUpdated={onTransactionUpdated} />,
    },
    {
      key: 'installment',
      header: t('budget.installment'),
      accessor: (r) =>
        r.installmentIndex && r.installmentTotal && r.installmentTotal > 1
          ? r.installmentIndex / r.installmentTotal
          : 0,
      cell: (r) =>
        r.installmentIndex && r.installmentTotal && r.installmentTotal > 1 ? (
          <Badge variant="accent">
            {r.installmentIndex}/{r.installmentTotal}
          </Badge>
        ) : (
          <span className={styles.muted}>-</span>
        ),
    },
    {
      key: 'category',
      header: t('budget.category'),
      accessor: (r) => (r.categoryId && categoriesById[r.categoryId] ? categoryLabel(categoriesById[r.categoryId]) : ''),
      cell: (r) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Select
            size="sm"
            value={r.categoryId || ''}
            onChange={(e) => onCategoryChange(r.id, e.target.value)}
          >
            <option value="">{t('budget.uncategorized')}</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>
                {categoryLabel(c)}
              </option>
            ))}
          </Select>
          {r.userEdited && (
            <Badge variant="neutral" className={styles.mt1}>
              {t('budget.edited')}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'receipt',
      header: t('budget.receipt'),
      align: 'end',
      accessor: (r) => (r.receiptLink ? 1 : 0),
      cell: (r) => (
        <div onClick={(e) => e.stopPropagation()}>
          {r.receiptLink ? (
            <Button size="sm" variant="ghost" onClick={() => setLinkModalTx(r)}>
              <Icon name="fa-receipt" size="sm" />
              {t('budget.receiptLinked')}
            </Button>
          ) : (
            Number(r.amount) > 0 && (
              <Button size="sm" variant="secondary" onClick={() => setLinkModalTx(r)}>
                <Icon name="fa-link" size="sm" />
                {t('budget.linkReceipt')}
              </Button>
            )
          )}
        </div>
      ),
    },
  ];

  if (loading) return <Container><Spinner /></Container>;
  if (error) return <Container><Alert variant="danger">{error}</Alert></Container>;
  if (!statement) return null;

  return (
    <Container>
      <button className={styles.backBtn} onClick={() => navigate('/budget/statements')}>
        <Icon name="fa-arrow-left" size="sm" />
        {t('back')}
      </button>

      <Card className={styles.mb4}>
        <Card.Header>
          <h5 className={styles.title}>
            {card
              ? `${card.bank || ''} ${card.cardName || ''} ${card.last4 ? `**${card.last4}` : ''}`.trim()
              : `#${statement.cardId}`}
          </h5>
        </Card.Header>
        <Card.Body>
          <div className={styles.infoRow}>
            <div>
              <div className={styles.mutedSmall}>{t('budget.period')}</div>
              <div>
                {formatShortDate(statement.periodStart, language)} →{' '}
                {formatShortDate(statement.periodEnd, language)}
              </div>
            </div>
            <div>
              <div className={styles.mutedSmall}>{t('budget.totalDue')}</div>
              <div className={styles.bold}>{formatTry(statement.totalDueTry)}</div>
            </div>
            {statement.minimumPayment != null && (
              <div>
                <div className={styles.mutedSmall}>{t('budget.minimumPayment')}</div>
                <div>{formatTry(statement.minimumPayment)}</div>
              </div>
            )}
            {statement.dueDate && (
              <div>
                <div className={styles.mutedSmall}>{t('budget.dueDate')}</div>
                <div>{formatShortDate(statement.dueDate, language)}</div>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      <div className={styles.txHeader}>
        <h6 className={styles.title}>
          {t('budget.transactions')} ({transactions.length})
          <span className={styles.linkedCount}>
            {t('budget.receiptsLinkedCount', { count: linkedCount })}
          </span>
        </h6>
        <Button size="sm" variant="secondary" onClick={onMatchReceipts} disabled={matching}>
          <Icon name="fa-sync" size="sm" />
          {matching ? t('budget.matchingReceipts') : t('budget.matchReceipts')}
        </Button>
      </div>

      {reviewCount > 0 && (
        <Alert variant="warning" className={styles.mb4}>
          {t('budget.amountsNeedReview', { count: reviewCount })}
        </Alert>
      )}

      <SectionedTable
        data={transactions}
        columns={txColumns}
        rowKey={(r) => r.id}
        groupBy={{
          key: (row) => monthKey(row.transactionDate) || '0000-00',
          label: (row) => formatLongMonth(row.transactionDate, language),
          order: (a, b) => b.localeCompare(a),
          trailing: (rows) => {
            const sum = rows.reduce((acc, r) => acc + Number(r.amount || 0), 0);
            return `${rows.length} • ${formatTry(sum)}`;
          },
        }}
        initialSort={{ key: 'date', dir: 'desc' }}
        emptyMessage={t('noData')}
      />

      {linkModalTx && (
        <ReceiptLinkModal
          transaction={linkModalTx}
          onClose={() => setLinkModalTx(null)}
          onChanged={(link) => onLinkChanged(linkModalTx.id, link)}
        />
      )}
    </Container>
  );
};

export default StatementDetail;
